import React, { useState, useEffect } from 'react';
import { commissionAPI, leadAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  DollarSign, Clock, Check, AlertTriangle, Plus, TrendingUp,
  Banknote, Receipt, FileText, CreditCard, IndianRupee, Percent,
  User, Building2, Phone, Mail, MapPin, Home, Calendar, 
  ChevronRight, X, Trophy, Layers
} from 'lucide-react';

const TIER_COLORS = {
  bronze: 'bg-orange-100 text-orange-700',
  silver: 'bg-slate-200 text-slate-700',
  gold: 'bg-amber-100 text-amber-700',
  platinum: 'bg-violet-100 text-violet-700',
};

const CommissionsPage = () => {
  const { hasRole } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, commission: null });
  const [activeTab, setActiveTab] = useState('all');
  const [paymentRef, setPaymentRef] = useState('');

  const [newCommission, setNewCommission] = useState({
    project_id: '',
    lead_id: '',
    booking_value: '',
    commission_percentage: '2',
  });

  const canManage = hasRole(['builder_admin']);
  const isPartner = hasRole(['partner', 'sub_user']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [commissionsRes, leadsRes] = await Promise.all([
        commissionAPI.list(),
        leadAPI.list({ status: 'booked' }),
      ]);
      setCommissions(commissionsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommission = async (e) => {
    e.preventDefault();
    try {
      const lead = leads.find(l => l.id === newCommission.lead_id);
      await commissionAPI.create({
        project_id: lead?.project_id || newCommission.project_id,
        lead_id: newCommission.lead_id,
        booking_value: parseFloat(newCommission.booking_value),
        commission_percentage: parseFloat(newCommission.commission_percentage),
      });
      toast.success('Commission record created');
      setDialogOpen(false);
      setNewCommission({ project_id: '', lead_id: '', booking_value: '', commission_percentage: '2' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create commission');
    }
  };

  const handlePay = async () => {
    if (!paymentRef.trim()) {
      toast.error('Please enter payment reference');
      return;
    }
    try {
      await commissionAPI.pay(detailDialog.commission.id, paymentRef);
      toast.success('Commission marked as paid');
      setDetailDialog({ open: false, commission: null });
      setPaymentRef('');
      loadData();
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  const openDetailDialog = (commission) => {
    setDetailDialog({ open: true, commission });
    setPaymentRef('');
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { class: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending' },
      'processing': { class: 'bg-blue-100 text-blue-700 border-blue-200', icon: Receipt, label: 'Processing' },
      'paid': { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Check, label: 'Paid' },
      'disputed': { class: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, label: 'Disputed' },
    };
    return configs[status] || configs.pending;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredCommissions = commissions.filter((c) => {
    return activeTab === 'all' || c.status === activeTab;
  });

  const stats = {
    totalEarned: commissions.reduce((sum, c) => sum + (c.commission_amount || 0), 0),
    pending: commissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + (c.commission_amount || 0), 0),
    paid: commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + (c.commission_amount || 0), 0),
    totalBookings: commissions.reduce((sum, c) => sum + (c.booking_value || 0), 0),
    count: {
      total: commissions.length,
      pending: commissions.filter(c => c.status === 'pending').length,
      paid: commissions.filter(c => c.status === 'paid').length,
    }
  };

  const paidPercentage = stats.totalEarned > 0 ? (stats.paid / stats.totalEarned) * 100 : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commissions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote className="h-7 w-7 text-emerald-600" />
            Commission Tracking
          </h1>
          <p className="text-muted-foreground">Track earnings and payouts transparently</p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-add-commission">
                <Plus className="h-4 w-4 mr-2" />
                Add Commission
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Commission Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCommission} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Booked Lead *</Label>
                  <Select value={newCommission.lead_id} onValueChange={(v) => setNewCommission({...newCommission, lead_id: v})} required>
                    <SelectTrigger><SelectValue placeholder="Select booked lead" /></SelectTrigger>
                    <SelectContent>
                      {leads.filter(l => l.status === 'booked').map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name} - {l.phone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Booking Value (₹) *</Label>
                  <Input 
                    type="number" 
                    value={newCommission.booking_value} 
                    onChange={(e) => setNewCommission({...newCommission, booking_value: e.target.value})} 
                    required 
                    placeholder="e.g., 7500000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commission % *</Label>
                  <Select value={newCommission.commission_percentage} onValueChange={(v) => setNewCommission({...newCommission, commission_percentage: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1%</SelectItem>
                      <SelectItem value="1.5">1.5%</SelectItem>
                      <SelectItem value="2">2%</SelectItem>
                      <SelectItem value="2.5">2.5%</SelectItem>
                      <SelectItem value="3">3%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newCommission.booking_value && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-sm text-emerald-700">
                      Commission Amount: <span className="font-bold text-lg">
                        {formatCurrency(parseFloat(newCommission.booking_value) * parseFloat(newCommission.commission_percentage) / 100)}
                      </span>
                    </p>
                  </div>
                )}
                <Button type="submit" className="w-full">Create Commission</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalBookings)}</p>
            <p className="text-xs text-muted-foreground">{stats.count.total} deals</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <IndianRupee className="h-4 w-4" />
              <span className="text-sm">Total Commission</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.totalEarned)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Pending Payout</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{formatCurrency(stats.pending)}</p>
            <p className="text-xs text-muted-foreground">{stats.count.pending} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <CreditCard className="h-4 w-4" />
              <span className="text-sm">Paid Out</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(stats.paid)}</p>
            <p className="text-xs text-muted-foreground">{stats.count.paid} paid</p>
          </CardContent>
        </Card>
      </div>

      {/* Payout Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Payout Progress</span>
            <span className="text-sm text-muted-foreground">{paidPercentage.toFixed(0)}% paid</span>
          </div>
          <Progress value={paidPercentage} className="h-3" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Paid: {formatCurrency(stats.paid)}</span>
            <span>Pending: {formatCurrency(stats.pending)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.count.total})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({stats.count.pending})</TabsTrigger>
          <TabsTrigger value="paid">Paid ({stats.count.paid})</TabsTrigger>
          <TabsTrigger value="disputed">Disputed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Commissions List */}
      {filteredCommissions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No commissions found</p>
            <p className="text-muted-foreground">Commission records will appear here when bookings are confirmed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCommissions.map((commission) => {
            const statusConfig = getStatusConfig(commission.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={commission.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  commission.status === 'pending' ? 'border-amber-200' : ''
                }`}
                onClick={() => openDetailDialog(commission)}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                        commission.status === 'paid' ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        <Banknote className={`h-6 w-6 ${
                          commission.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-lg">
                            {formatCurrency(commission.commission_amount)}
                          </h3>
                          <Badge className={statusConfig.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          {commission.partner?.tier && (
                            <Badge className={TIER_COLORS[commission.partner.tier]}>
                              <Trophy className="h-3 w-3 mr-1" />
                              {commission.partner.tier}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          {commission.commission_percentage}% of {formatCurrency(commission.booking_value)}
                        </p>
                        
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                          {commission.customer && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <User className="h-3 w-3" />
                              {commission.customer.name}
                            </span>
                          )}
                          {commission.project && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Building2 className="h-3 w-3" />
                              {commission.project.name}
                            </span>
                          )}
                          {commission.partner && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <User className="h-3 w-3" />
                              Partner: {commission.partner.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        View Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Commission Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, commission: open ? detailDialog.commission : null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailDialog.commission && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-emerald-600" />
                  Commission Details
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Commission Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-emerald-700">Commission Amount</p>
                      <p className="text-3xl font-bold text-emerald-800">
                        {formatCurrency(detailDialog.commission.commission_amount)}
                      </p>
                    </div>
                    <Badge className={getStatusConfig(detailDialog.commission.status).class + " text-sm py-1 px-3"}>
                      {React.createElement(getStatusConfig(detailDialog.commission.status).icon, { className: "h-4 w-4 mr-1" })}
                      {getStatusConfig(detailDialog.commission.status).label}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-emerald-600">Booking Value:</span>
                      <span className="font-semibold ml-2">{formatCurrency(detailDialog.commission.booking_value)}</span>
                    </div>
                    <div>
                      <span className="text-emerald-600">Commission Rate:</span>
                      <span className="font-semibold ml-2">{detailDialog.commission.commission_percentage}%</span>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                {detailDialog.commission.customer && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600" />
                      Customer Details
                    </h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{detailDialog.commission.customer.name}</p>
                              <p className="text-sm text-muted-foreground">Customer</p>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <p className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              {detailDialog.commission.customer.phone}
                            </p>
                            {detailDialog.commission.customer.email && (
                              <p className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                {detailDialog.commission.customer.email}
                              </p>
                            )}
                          </div>
                          <div className="md:col-span-2 flex flex-wrap gap-4 text-sm pt-2 border-t">
                            <span><strong>Preference:</strong> {detailDialog.commission.customer.bhk_preference || '-'}</span>
                            <span><strong>Budget:</strong> {formatCurrency(detailDialog.commission.customer.budget)}</span>
                            <span><strong>Booked:</strong> {formatDate(detailDialog.commission.customer.booked_at)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Project & Unit Details */}
                {detailDialog.commission.project && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-violet-600" />
                      Project & Unit Details
                    </h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="font-semibold text-lg">{detailDialog.commission.project.name}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {detailDialog.commission.project.location}
                            </p>
                            {detailDialog.commission.project.rera_number && (
                              <p className="text-xs text-muted-foreground mt-1">
                                RERA: {detailDialog.commission.project.rera_number}
                              </p>
                            )}
                          </div>
                          {detailDialog.commission.unit ? (
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="font-semibold flex items-center gap-2">
                                <Home className="h-4 w-4" />
                                Unit {detailDialog.commission.unit.unit_number}
                              </p>
                              <div className="text-sm text-muted-foreground mt-1 space-y-1">
                                <p>Floor: {detailDialog.commission.unit.floor}</p>
                                <p>Type: {detailDialog.commission.unit.bhk_type}</p>
                                <p>Area: {detailDialog.commission.unit.carpet_area} sq.ft</p>
                                <p>Price: {formatCurrency(detailDialog.commission.unit.price)}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-slate-50 rounded-lg p-3 text-sm text-muted-foreground">
                              <p className="flex items-center gap-2">
                                <Home className="h-4 w-4" />
                                Unit details not linked
                              </p>
                              <p className="mt-1">Preference: {detailDialog.commission.customer?.bhk_preference || '-'}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Partner Details */}
                {detailDialog.commission.partner && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <User className="h-5 w-5 text-amber-600" />
                      Channel Partner
                    </h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{detailDialog.commission.partner.name}</p>
                              <p className="text-sm text-muted-foreground">{detailDialog.commission.partner.company}</p>
                            </div>
                          </div>
                          <Badge className={TIER_COLORS[detailDialog.commission.partner.tier || 'bronze']}>
                            <Trophy className="h-3 w-3 mr-1" />
                            {detailDialog.commission.partner.tier || 'Bronze'} Tier
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Payment Schedule */}
                {detailDialog.commission.payment_schedule && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-indigo-600" />
                      Customer Payment Schedule
                    </h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {detailDialog.commission.payment_schedule.map((milestone, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                milestone.status === 'paid' ? 'bg-emerald-50 border border-emerald-200' :
                                milestone.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
                                'bg-slate-50 border border-slate-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  milestone.status === 'paid' ? 'bg-emerald-200 text-emerald-700' :
                                  milestone.status === 'pending' ? 'bg-amber-200 text-amber-700' :
                                  'bg-slate-200 text-slate-600'
                                }`}>
                                  {milestone.status === 'paid' ? <Check className="h-4 w-4" /> : idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium">{milestone.milestone}</p>
                                  <p className="text-xs text-muted-foreground">{milestone.percentage}% of booking value</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{formatCurrency(milestone.amount)}</p>
                                <Badge variant="outline" className={`text-xs ${
                                  milestone.status === 'paid' ? 'text-emerald-600' :
                                  milestone.status === 'pending' ? 'text-amber-600' :
                                  'text-slate-500'
                                }`}>
                                  {milestone.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Payment Section */}
                {detailDialog.commission.status === 'paid' ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Check className="h-5 w-5" />
                      <span className="font-semibold">Payment Completed</span>
                    </div>
                    <div className="mt-2 text-sm text-emerald-600">
                      <p>Paid on: {formatDate(detailDialog.commission.paid_at)}</p>
                      {detailDialog.commission.payment_reference && (
                        <p>Reference: {detailDialog.commission.payment_reference}</p>
                      )}
                    </div>
                  </div>
                ) : canManage && detailDialog.commission.status === 'pending' ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-amber-600" />
                      Process Payment
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label>Payment Reference / UTR Number</Label>
                        <Input 
                          value={paymentRef} 
                          onChange={(e) => setPaymentRef(e.target.value)} 
                          placeholder="Enter UTR or transaction reference"
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={handlePay} 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Confirm Payment of {formatCurrency(detailDialog.commission.commission_amount)}
                      </Button>
                    </div>
                  </div>
                ) : isPartner && detailDialog.commission.status === 'pending' ? (
                  <div className="flex justify-end">
                    <Button variant="outline" className="text-orange-600">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Raise Dispute
                    </Button>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionsPage;
